import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import * as authApi from "../api/auth.api";
import { clearClientsCache } from "./ClientsContext";
import { clearPropertiesCache } from "./PropertiesContext";
import { clearDashboardCache } from "./DashboardContext";

const AuthContext = createContext(null);
const SESSION_KEY = "myv_session";
const PENDING_KEY = "myv_pending";
const SESSION_ACTIVE_KEY = "myv_session_active";
const TOKEN_KEY = "myv_token";
const TOKEN_EXPIRES_KEY = "myv_token_expires_at";
const PLATFORM_CONTEXT_KEY = "myv_platform_context";

const readStorage = (key) => {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch (error) {
    return null;
  }
};

const writeStorage = (key, value) => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (error) {
    // Safe no-op for environments without storage.
  }
};

const clearStorage = (key) => {
  try {
    localStorage.removeItem(key);
  } catch (error) {
    // Safe no-op for environments without storage.
  }
};

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => readStorage(SESSION_KEY));
  const [pendingUser, setPendingUser] = useState(() => readStorage(PENDING_KEY));
  const [pendingOtpDelivery, setPendingOtpDelivery] = useState([]);
  const [pendingOtpLength, setPendingOtpLength] = useState(6);
  const [isLoading, setIsLoading] = useState(true);
  const [sessionExpired, setSessionExpired] = useState(false);

  useEffect(() => {
    const activeFlag = readStorage(SESSION_ACTIVE_KEY);
    if (!user && activeFlag) {
      setSessionExpired(true);
    }
    const timer = setTimeout(() => setIsLoading(false), 300);
    return () => clearTimeout(timer);
  }, [user]);

  useEffect(() => {
    const handleStorage = (event) => {
      if (event.key === SESSION_KEY && !event.newValue) {
        setSessionExpired(true);
      }
    };
    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, []);

  const login = async (email, password) => {
    try {
      setPendingOtpDelivery([]);
      const data = await authApi.login({ email, password });
      const nextCode = data?.code;
      const payload = data?.data || {};
      if (nextCode === "OTP_REQUIRED" || nextCode === "MANUAL_OTP_REQUIRED") {
        const pending = { email };
        setPendingUser(pending);
        writeStorage(PENDING_KEY, pending);
        setPendingOtpDelivery(payload?.otpDelivery || []);
        setPendingOtpLength(payload?.otpLength || 6);
        return { ok: true, code: nextCode, data: payload };
      }
      return {
        ok: false,
        code: nextCode || "AUTH_FAILED",
        message: data?.message || "Login failed",
      };
    } catch (error) {
      return { ok: false, code: "AUTH_FAILED", message: "Login failed" };
    }
  };

  const signup = async (payload) => {
    try {
      setPendingOtpDelivery([]);
      const data = await authApi.signup({
        email: payload.email,
        password: payload.password,
      });
      const nextCode = data?.code;
      const payloadData = data?.data || {};
      if (nextCode === "OTP_REQUIRED" || nextCode === "MANUAL_OTP_REQUIRED") {
        const pending = {
          email: payload.email,
        };
        setPendingUser(pending);
        writeStorage(PENDING_KEY, pending);
        setPendingOtpDelivery(payloadData?.otpDelivery || []);
        setPendingOtpLength(payloadData?.otpLength || 6);
        return { ok: true, code: nextCode, data: payloadData };
      }
      return {
        ok: false,
        code: nextCode || "AUTH_FAILED",
        message: data?.message || "Signup failed",
      };
    } catch (error) {
      return { ok: false, code: "AUTH_FAILED", message: "Signup failed" };
    }
  };

  const verifyOtp = async (otp) => {
    if (!pendingUser?.email) {
      return { ok: false };
    }
    try {
      const data = await authApi.verifyOtp({
        otp,
        email: pendingUser.email,
      });
      if (data?.status && data.status !== "success") {
        return { ok: false, code: data?.code, message: data?.message };
      }
      const payload = data?.data ?? null;
      const token =
        (payload && typeof payload === "object" && payload.token) ||
        (payload && typeof payload === "object" && payload.jwt) ||
        (typeof payload === "string" ? payload : "");
      if (!token) {
        return { ok: false, code: data?.code || "AUTH_FAILED", message: data?.message };
      }
      const nextUser = pendingUser || { email: pendingUser.email };
      setUser(nextUser);
      writeStorage(SESSION_KEY, nextUser);
      writeStorage(SESSION_ACTIVE_KEY, true);
      writeStorage(TOKEN_KEY, token);
      if (payload && typeof payload === "object" && payload.expiresIn) {
        writeStorage(TOKEN_EXPIRES_KEY, Date.now() + payload.expiresIn * 1000);
      } else {
        clearStorage(TOKEN_EXPIRES_KEY);
      }
      setPendingUser(null);
      setPendingOtpDelivery([]);
      setPendingOtpLength(6);
      clearStorage(PENDING_KEY);
      return { ok: true, code: data?.code || "SUCCESS", data: payload };
    } catch (error) {
      return { ok: false, code: "AUTH_FAILED", message: "OTP verification failed" };
    }
  };

  const logout = () => {
    setUser(null);
    setPendingUser(null);
    setPendingOtpDelivery([]);
    setPendingOtpLength(6);
    clearStorage(SESSION_KEY);
    clearStorage(PENDING_KEY);
    clearStorage(SESSION_ACTIVE_KEY);
    clearStorage(TOKEN_KEY);
    clearStorage(TOKEN_EXPIRES_KEY);
    clearStorage(PLATFORM_CONTEXT_KEY);
    try {
      sessionStorage.removeItem(PLATFORM_CONTEXT_KEY);
    } catch (error) {
      // no-op
    }
    clearClientsCache();
    clearPropertiesCache();
    clearDashboardCache();
    setSessionExpired(false);
  };

  const value = useMemo(
    () => ({
      user,
      pendingUser,
      pendingOtpDelivery,
      pendingOtpLength,
      isLoading,
      sessionExpired,
      login,
      signup,
      verifyOtp,
      logout,
    }),
    [
      user,
      pendingUser,
      pendingOtpDelivery,
      pendingOtpLength,
      isLoading,
      sessionExpired,
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
